import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDrag } from 'react-dnd';
import { Icons } from '../Icons';
import { DisplaySetMessageListTooltip } from '../DisplaySetMessageListTooltip';
import { TooltipTrigger, TooltipContent, Tooltip } from '../Tooltip';
import { Button } from '../Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../DropdownMenu';
import { Icon, ProgressLoadingBar } from '@ohif/ui';
import { RiDeleteBin2Line } from 'react-icons/ri';
import { DicomMetadataStore } from '@ohif/core';
import { VscLoading } from 'react-icons/vsc';

/**
 * Display a thumbnail for a display set.
 */
const Thumbnail = ({
  studyInstanceUID,
  displaySetInstanceUID,
  className,
  imageSrc,
  imageAltText,
  description,
  seriesNumber,
  numInstances,
  loadingProgress,
  countIcon,
  messages,
  dragData = {},
  isActive,
  onClick,
  onDoubleClick,
  viewPreset = 'thumbnails',
  modality,
  isHydratedForDerivedDisplaySet = false,
  canReject = false,
  onReject = () => {},
  isTracked = false,
  thumbnailType = 'thumbnail',
  onClickUntrack = () => {},
  onThumbnailContextMenu,
  onClickDeleteSeries,
  onClickDeleteInstance,
  QC,
}: withAppTypes): React.ReactNode => {
  // TODO: We should wrap our thumbnail to create a "DraggableThumbnail", as
  // this will still allow for "drag", even if there is no drop target for the
  // specified item.
  const [collectedProps, drag, dragPreview] = useDrag({
    type: 'displayset',
    item: { ...dragData },
    canDrag: function (monitor) {
      return Object.keys(dragData).length !== 0;
    },
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        // Emit event when successfully dropped
        // const displaySets = displaySetService.getActiveDisplaySets();
        // const displaySet = displaySets[0];
        // const instance = displaySet?.instances?.[0] || displaySet?.instance;
        // console.warn('patientDroppedSuccess', studyInstanceUID);
        // Replace '*' with your specific parent origin
      } else {
        // Emit event when drag was cancelled
        // console.warn('patientDropped failed'); // Replace '*' with your specific parent origin
      }
    },
  });

  const [lastTap, setLastTap] = useState(0);

  // console.warn('loadingProgress ', loadingProgress);
  const [loadSeries, setLoadSeries] = useState(0);

  const handleTouchEnd = e => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    if (tapLength < 300 && tapLength > 0) {
      onDoubleClick(e);
    } else {
      onClick(e);
    }
    setLastTap(currentTime);
  };
  const [isInstanceOpt, setIsInstanceOpt] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const accessToken = localStorage.getItem('access_token');
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;

  // const [supportDelete, setSupportDelete] = useState(false);

  // useEffect(() => {
  //   const handleMessage = (event: MessageEvent) => {
  //     if (
  //       // event.origin === new URL(ohif).origin &&
  //       event.data?.menuSupport === 'studyqc'
  //     ) {
  //       setSupportDelete(true);
  //     }
  //     // else setSupportDelete(false);
  //   };

  //   window.addEventListener('message', handleMessage);

  //   console.warn('supportDelete ', supportDelete);

  //   // Cleanup listener saat komponen unmount
  //   return () => {
  //     window.removeEventListener('message', handleMessage);
  //   };
  // }, [displaySetInstanceUID]);

  useEffect(() => {
    if (loadingProgress) setLoadSeries(Math.round(Number(loadingProgress) * 100));
  }, [loadingProgress]);

  // const Accepted = DicomMetadataStore.getStudy(studyInstanceUID).Accepted;

  // console.warn('QC', QC);

  const renderThumbnailPreset = () => {
    return (
      <div
        className={classnames(
          'flex h-full w-full flex-col items-center justify-center gap-[2px] p-[4px]',
          isActive && 'bg-popover border-primary-light border'
        )}
      >
        <div className="h-[84px] w-[94px]">
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={imageAltText}
                className="h-[84px] w-[94px] rounded"
                crossOrigin="anonymous"
              />
            ) : (
              <div className={`bg-background h-[84px] w-[94px]`}>
                <div className="transparent flex h-full w-full items-center justify-center">
                  <VscLoading className="h-[40px] w-[40px] animate-spin text-white" />
                </div>
              </div>
            )}

            {accessToken && user && user.Role === 'SUPERADMIN' && QC && (
              <div
                className={`absolute bottom-0 right-0 z-50 flex h-[84px] w-[94px] flex-col items-center justify-center gap-2 rounded p-2 ${
                  isHovered || isInstanceOpt ? 'bg-[rgba(0,0,0,0.7)]' : 'hidden bg-[rgba(0,0,0,0)]'
                } transition-colors duration-200 ease-in-out`}
              >
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger>
                      <div className="group">
                        <Icons.Trash
                          className="h-[18px] w-[18px] text-red-500"
                          onClick={() => onClickDeleteSeries(displaySetInstanceUID)}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <div className="flex flex-1 flex-row">
                        <div className="flex-2 flex items-center justify-center pr-4">
                          <Icons.InfoLink className="text-primary-active" />
                        </div>
                        <div className="flex flex-1 flex-col">
                          <span>
                            <span className="text-white">Delete this series</span>
                          </span>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  {isActive && (
                    <DropdownMenu
                      open={isInstanceOpt}
                      onOpenChange={setIsInstanceOpt}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setIsInstanceOpt(true)}
                        >
                          <Icons.More title="" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem
                          onSelect={() => {
                            onClickDeleteInstance(displaySetInstanceUID);
                          }}
                          className="gap-[6px] p-1"
                        >
                          <RiDeleteBin2Line size={20} />
                          Delete Instance
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            )}

            {/* bottom left */}
            <div className="absolute bottom-0 left-0 flex h-[14px] w-full items-center justify-between gap-[4px] rounded-tr pt-[10px] pb-[8px] pr-[3px] pl-[3px]">
              <div className="flex items-center justify-start">
                <div
                  className={classnames(
                    'h-[10px] w-[10px] rounded-[2px]',
                    isActive || isHydratedForDerivedDisplaySet
                      ? 'bg-highlight'
                      : 'bg-highlight opacity-70'
                    // loadingProgress && loadingProgress < 1 && 'bg-highlight opacity-25'
                  )}
                ></div>
                <div
                  className="pt-[1px] text-[11px] font-semibold text-white"
                  style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.8)' }}
                >
                  &nbsp;{modality}
                </div>
              </div>
              <div
                className={classnames(
                  'text-muted-foreground justify-end pl-1 text-[11px]',
                  loadSeries === 100 && 'opacity-70'
                )}
                style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.8)' }}
              >
                {loadSeries}%
              </div>
            </div>

            {/* top right */}
            <div className="absolute top-0 right-0 z-50 flex items-center gap-[4px]">
              {/* <DisplaySetMessageListTooltip
                messages={messages}
                id={`display-set-tooltip-${displaySetInstanceUID}`}
              /> */}
              {isTracked && (
                <Tooltip>
                  <TooltipTrigger>
                    <div className="group">
                      <Icons.StatusTracking className="text-primary-light h-[20px] w-[20px] group-hover:hidden" />
                      <Icons.Cancel
                        className="text-primary-light hidden h-[15px] w-[15px] group-hover:block"
                        onClick={onClickUntrack}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <div className="flex flex-1 flex-row">
                      <div className="flex-2 flex items-center justify-center pr-4">
                        <Icons.InfoLink className="text-primary-active" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <span>
                          <span className="text-white">
                            {isTracked ? 'Series is tracked' : 'Series is untracked'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {/* bottom right */}
            <div className="absolute bottom-0 right-0 flex items-center gap-[4px] p-[4px]">
              {/* <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hidden group-hover:inline-flex data-[state=open]:inline-flex"
                  >
                    <Icons.More />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  hideWhenDetached
                  align="start"
                >
                  <DropdownMenuItem
                    onSelect={() => {
                      onThumbnailContextMenu('openDICOMTagViewer', {
                        displaySetInstanceUID,
                      });
                    }}
                    className="gap-[6px]"
                  >
                    <Icons.DicomTagBrowser />
                    Tag Browser
                  </DropdownMenuItem>
                  {canReject && (
                    <DropdownMenuItem
                      onSelect={() => {
                        onReject();
                      }}
                      className="gap-[6px]"
                    >
                      <Icons.Trash className="h-5 w-5 text-red-500" />
                      Delete Report
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu> */}
            </div>
          </div>
        </div>
        <div className="mt-3 flex h-[44px] w-[84px] flex-col">
          {loadSeries < 100 && (
            <div className={classnames('w-full pb-[2px]')}>
              <ProgressLoadingBar
                progress={loadSeries}
                // progress={loadingProgress ? Math.round(Number(loadingProgress) * 100) : 0}
                height={4}
              />
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="min-h-[18px] w-[84px] pb-0.5 text-[12px] font-normal leading-4 text-white">
                <span className="block w-full overflow-hidden truncate whitespace-nowrap">
                  {description}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className="flex flex-row">
                <div className="flex items-center justify-center pr-4">
                  <Icons.InfoLink className="text-primary-active" />
                </div>
                <div className="flex flex-col">
                  <span className="text-white">{description}</span>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>

          <div className="flex h-[12px] items-center gap-[7px] overflow-hidden">
            <div className="text-muted-foreground text-[11px]"> S:{seriesNumber}</div>
            <div className="text-muted-foreground text-[11px]">
              <div className="flex items-center gap-[4px]">
                {countIcon ? (
                  React.createElement(Icons[countIcon] || Icons.MissingIcon, { className: 'w-3' })
                ) : (
                  <Icons.InfoSeries className="w-3" />
                )}
                <div>{numInstances}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderListPreset = () => {
    return (
      <div
        className={classnames(
          'flex h-full w-full items-center justify-between pr-[8px] pl-[8px] pt-[4px] pb-[4px]',
          isActive && 'bg-popover'
        )}
      >
        <div className="relative flex h-[32px] items-center gap-[8px]">
          <div
            className={classnames(
              'h-[32px] w-[4px] rounded-[2px]',
              isActive || isHydratedForDerivedDisplaySet ? 'bg-highlight' : 'bg-primary/65',
              loadingProgress && loadingProgress < 1 && 'bg-primary/25'
            )}
          ></div>
          <div className="flex h-full flex-col">
            <div className="flex items-center gap-[7px]">
              <div className="text-[13px] font-semibold text-white">{modality}</div>

              <div className="max-w-[160px] overflow-hidden overflow-ellipsis whitespace-nowrap text-[13px] font-normal text-white">
                {description}
              </div>
            </div>

            <div className="flex h-[12px] items-center gap-[7px] overflow-hidden">
              <div className="text-muted-foreground text-[12px]"> S:{seriesNumber}</div>
              <div className="text-muted-foreground text-[12px]">
                <div className="flex items-center gap-[4px]">
                  {' '}
                  {countIcon ? (
                    React.createElement(Icons[countIcon] || Icons.MissingIcon, { className: 'w-3' })
                  ) : (
                    <Icons.InfoSeries className="w-3" />
                  )}
                  <div>{numInstances}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex h-full items-center gap-[4px]">
          <DisplaySetMessageListTooltip
            messages={messages}
            id={`display-set-tooltip-${displaySetInstanceUID}`}
          />

          {isTracked && (
            <Tooltip>
              <TooltipTrigger>
                <div className="group">
                  <Icons.StatusTracking className="text-primary-light h-[20px] w-[20px] group-hover:hidden" />
                  <Icons.Cancel
                    className="text-primary-light hidden h-[15px] w-[15px] group-hover:block"
                    onClick={onClickUntrack}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <div className="flex flex-1 flex-row">
                  <div className="flex-2 flex items-center justify-center pr-4">
                    <Icons.InfoLink className="text-primary-active" />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <span>
                      <span className="text-white">
                        {isTracked ? 'Series is tracked' : 'Series is untracked'}
                      </span>
                    </span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="hidden group-hover:inline-flex data-[state=open]:inline-flex"
              >
                <Icons.More />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent hideWhenDetached>
              <DropdownMenuItem
                onSelect={() => {
                  onThumbnailContextMenu('openDICOMTagViewer', {
                    displaySetInstanceUID,
                  });
                }}
                className="gap-[6px]"
              >
                <Icons.DicomTagBrowser />
                Tag Browser
              </DropdownMenuItem>
              {canReject && (
                <DropdownMenuItem
                  onSelect={() => {
                    onReject();
                  }}
                  className="gap-[6px]"
                >
                  <Icons.Trash className="h-5 w-5 text-red-500" />
                  Delete Report
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  return (
    <div
      className={classnames(
        className,
        'bg-popover hover:bg-popover/30 hover:border-primary-light group flex cursor-pointer select-none flex-col rounded outline-none hover:border',
        viewPreset === 'thumbnails' && 'h-[136px] w-[100px]',
        viewPreset === 'list' && 'col-span-2 h-[40px] w-[206px]'
      )}
      id={`thumbnail-${displaySetInstanceUID}`}
      data-cy={
        thumbnailType === 'thumbnailNoImage'
          ? 'study-browser-thumbnail-no-image'
          : 'study-browser-thumbnail'
      }
      data-series={seriesNumber}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onTouchEnd={handleTouchEnd}
      role="button"
    >
      <div
        ref={drag}
        className="h-full w-full"
      >
        {viewPreset === 'thumbnails' && renderThumbnailPreset()}
        {viewPreset === 'list' && renderListPreset()}
      </div>
    </div>
  );
};

Thumbnail.propTypes = {
  studyInstanceUID: PropTypes.string,
  displaySetInstanceUID: PropTypes.string.isRequired,
  className: PropTypes.string,
  imageSrc: PropTypes.string,
  /**
   * Data the thumbnail should expose to a receiving drop target. Use a matching
   * `dragData.type` to identify which targets can receive this draggable item.
   * If this is not set, drag-n-drop will be disabled for this thumbnail.
   *
   * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
   */
  dragData: PropTypes.shape({
    /** Must match the "type" a dropTarget expects */
    type: PropTypes.string.isRequired,
  }),
  imageAltText: PropTypes.string,
  description: PropTypes.string.isRequired,
  seriesNumber: PropTypes.any,
  numInstances: PropTypes.number.isRequired,
  loadingProgress: PropTypes.number,
  messages: PropTypes.object,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  viewPreset: PropTypes.string,
  modality: PropTypes.string,
  isHydratedForDerivedDisplaySet: PropTypes.bool,
  canReject: PropTypes.bool,
  onReject: PropTypes.func,
  isTracked: PropTypes.bool,
  onClickUntrack: PropTypes.func,
  countIcon: PropTypes.string,
  thumbnailType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage']),
  onClickDeleteSeries: PropTypes.func,
  onClickDeleteInstance: PropTypes.func,
  QC: PropTypes.bool,
};

export { Thumbnail };
